



















































































































































































































import { Component, Mixins, Watch } from "vue-property-decorator";
import Search from "@/components/elements/utility/Search.vue";
import DataTable from "@/components/elements/utility/Table.vue";
import Pagination from "@/components/elements/utility/Pagination.vue";
import StickyHScroll from "@/components/elements/utility/StickyHScroll.vue";
import { Order } from "@/models/order/order";
import { NetSuiteQuoteStatus } from "@/models/netsuite/helpers";
import { NetSuiteCustomer } from "@/models/netsuite/netsuite";
import ConfirmNewQuote from "@/components/elements/modal/ConfirmNewQuote.vue";
import SalesOrderTable from "@/components/orders/SalesOrderTable.vue";
import { OrderMixin } from "@/mixins/orders/mixin";
import { SalesOrderPaging } from "@/mixins/orders/sales_order_paging";
import { CustomerMixin } from "@/mixins/customers/mixin";
import { Mobile } from "@/mixins/mobile";
import ConfirmClearAll from "@/components/elements/modal/ConfirmClearAll.vue";
import ConfirmDelete from "@/components/elements/modal/ConfirmDelete.vue";
import { GenericError } from "@/services/error_service";
import { NotificationService } from "@/services/notification_service";

@Component({
  components: {
    Search,
    DataTable,
    Pagination,
    SalesOrderTable,
    StickyHScroll,
    ConfirmNewQuote,
    ConfirmClearAll,
    ConfirmDelete
  }
})
export default class CustomerOrders extends Mixins(
  Mobile,
  OrderMixin,
  CustomerMixin,
  SalesOrderPaging
) {
  protected customer: NetSuiteCustomer = {} as NetSuiteCustomer;
  protected id = "";
  protected loader: any = null;
  protected showModalNew = false;
  // FUPOF-200: this customer's NetSuite Sales Orders live in SalesOrderPaging
  // rather than being folded into `orders` — the mixin's tab pipeline
  // (sortedOrders, tabCounts, filteredOrders, effectiveStatus) is entirely
  // quote-shaped, and a SalesOrderSummary has no _id, contact_details, items or
  // created_at to give it. They are paged through the API: one customer has
  // 7,365 orders, too many to hold whole.
  protected salesOrdersTabKey = "Sales Orders";

  // Getter (not a data field): field initializers run before mixin data is
  // merged onto the instance, so this.draftsTabKey would be undefined there.
  // No Legacy tab: legacy (Mongo) customers carry no NetSuite id, so legacy
  // quotes cannot be tied to the NetSuite customer this page is scoped to.
  protected get tabs(): Array<{ key: string; label: string }> {
    return [
      { key: this.draftsTabKey, label: "Drafts" },
      { key: NetSuiteQuoteStatus.Open, label: "Open" },
      { key: NetSuiteQuoteStatus.Processed, label: "Processed" },
      { key: NetSuiteQuoteStatus.Expired, label: "Expired" },
      { key: this.salesOrdersTabKey, label: "Sales Orders" }
    ];
  }

  protected get isSalesOrdersTab(): boolean {
    return this.activeTab === this.salesOrdersTabKey;
  }

  /**
   * FUPOF-200: the count pill for one tab. The mixin's `tabCounts` is built from
   * six hardcoded quote buckets, so `tabCounts["Sales Orders"]` is `undefined` —
   * which renders as a blank pill rather than 0, and makes the auto-select check
   * in created() skip the tab. Route every count read through here.
   */
  protected tabCount(key: string): number {
    // The unsearched total, like the quote pills, which never shrink with a search.
    return key === this.salesOrdersTabKey
      ? this.salesOrdersBrowseTotal
      : this.tabCounts[key] ?? 0;
  }

  /** Every sales order request on this page is for this customer. */
  protected salesOrderScope(): { customer_id?: number } {
    return { customer_id: Number(this.id) };
  }

  /**
   * The Sales Orders tab searches on the server. The search box is shared with
   * the quote tabs, so a term typed elsewhere is applied on arriving here.
   */
  @Watch("search")
  @Watch("activeTab")
  protected syncSalesOrderSearch(): void {
    if (this.isSalesOrdersTab) {
      this.queueSalesOrderSearch(this.search);
    }
  }

  // The Processed tab is the only one with a Sales Order # column, so it is the
  // only tab whose search matches on it (see OrderMixin.filteredOrders).
  protected get searchPlaceholder(): string {
    if (this.isSalesOrdersTab) {
      return "Search by Sales Order # or PO #";
    }
    return this.activeTab === NetSuiteQuoteStatus.Processed
      ? "Search by Quote Name, Quote #, or Sales Order #"
      : "Search by Quote Name or Quote #";
  }

  async created() {
    this.showLoader("Obtaining Quotes...");
    if (this.$route.params.id) {
      this.id = this.$route.params.id as string;
      // The customer lookup only feeds the loader label and header; both quote
      // fetches key off the route id, so none of these need to wait on another.
      await Promise.all([
        this.prepareCustomer(),
        this.prepareNetSuiteQuotes(),
        this.prepareDrafts(),
        this.prepareSalesOrders()
      ]);
      this.orders = this.convertDraftsToOrders(this.drafts).concat(
        this.netSuiteQuotes
      );
      // FUPOF-200: via tabCount, not tabCounts — the mixin's map has no entry
      // for the Sales Orders tab, so a customer whose only history is sales
      // orders would otherwise land on an empty Open tab.
      if (this.tabCount(this.draftsTabKey) > 0) {
        this.activeTab = this.draftsTabKey;
      } else if (this.tabCount(this.activeTab) === 0) {
        const firstNonEmpty = this.tabs.find(t => this.tabCount(t.key) > 0);
        if (firstNonEmpty) {
          this.activeTab = firstNonEmpty.key;
        }
      }
      this.closeLoader();
    } else {
      this.closeLoader();
      this.$router.replace("/customers");
    }
  }

  protected showDeleteModal = false;
  protected draftPendingDelete: Order | null = null;

  protected askDeleteDraft(order: Order): void {
    this.draftPendingDelete = order;
    this.showDeleteModal = true;
  }

  protected closeDeleteDraft(): void {
    this.draftPendingDelete = null;
    this.showDeleteModal = false;
  }

  protected async confirmDeleteDraft(): Promise<void> {
    const order = this.draftPendingDelete;
    this.closeDeleteDraft();
    if (!order?.draft_id) return;
    try {
      await this.deleteDraftAction(order.draft_id);
      this.drafts = this.drafts.filter(d => d.id !== order.draft_id);
      this.orders = this.orders.filter(o => o.draft_id !== order.draft_id);
      NotificationService.show("Draft deleted.", "success");
    } catch (err) {
      GenericError.popup(err.message);
    }
  }

  protected async prepareCustomer(): Promise<void> {
    this.customer = await this.netSuiteService.getNetSuiteCustomerById(this.id);
    this.showLoader(`Obtaining ${this.customer.company_name} Quotes...`);
  }

  protected async prepareDrafts(): Promise<void> {
    try {
      // Drafts are private to the current user; scope them to this customer.
      const myDrafts = await this.quoteDraftService.listQuotes();
      this.drafts = myDrafts.filter(
        d => d.netsuite_customer_id === Number(this.id)
      );
    } catch (err) {
      console.warn("Quote drafts unavailable:", err);
      this.drafts = [];
    }
  }

  /**
   * FUPOF-200: this customer's sales orders, for the reorder tab. `this.id` is
   * the route param and IS the NetSuite customer id, so this needs nothing from
   * prepareCustomer() and can run alongside it.
   *
   * Failures degrade to an empty tab rather than an error, matching how
   * prepareDrafts treats an unreachable source (see loadSalesOrders).
   */
  protected async prepareSalesOrders(): Promise<void> {
    await this.loadSalesOrders(1, "");
  }

  protected async prepareNetSuiteQuotes(): Promise<void> {
    const customerNetSuiteQuotes = await this.netSuiteService.getCustomerNetSuiteQuotes(
      parseInt(this.id)
    );
    // // ToDo: FNSI-101 - get the quote line items for each quote in the array.
    // const netSuiteQuotesWithLineItems = await this.getNetSuiteQuoteLineItems(
    //   customerNetSuiteQuotes
    // );
    // console.log(
    //   "decide what to do with netSuiteQuotesWithLineItems",
    //   netSuiteQuotesWithLineItems
    // );
    // this.convertNetSuiteQuotes(netSuiteQuotesWithLineItems);
    this.convertNetSuiteQuotes(customerNetSuiteQuotes);
  }

  protected closeModal(): void {
    this.showModalNew = false;
  }

  protected async clearOrder(): Promise<void> {
    await this.resetOrder();
    await this.reassignQuote();
  }

  protected toggleNewQuoteModal(): void {
    // getOrderItems can return undefined, so guard before reading .length —
    // otherwise the click handler throws and the link silently does nothing.
    if (this.items && this.items.length > 0) {
      // only toggling warning modal if there is an item associated with the order
      this.showModalNew = true;
    } else {
      this.reassignQuote();
    }
  }

  protected async reassignQuote() {
    // prepareCustomer() may have left this.customer empty (getNetSuiteCustomerById
    // swallows errors and returns {}). Without this guard, customer_id.toString()
    // throws and the promise rejects unhandled — the "Start a quote" link appears
    // to do nothing. Surface the problem instead of failing silently.
    if (!this.customer?.customer_id) {
      GenericError.popup(
        "Customer details could not be loaded. Please refresh and try again."
      );
      return;
    }
    try {
      await this.saveCustomerToStore(
        this.customer.customer_id.toString(),
        this.customer.company_name,
        1 + (this.customer.price_level_discount ?? 0),
        this.customer.customer_id,
        this.customer.payment_terms_id || undefined,
        this.customer.price_level_id,
        this.customer.price_level_discount ?? undefined,
        null
      );
    } catch (err) {
      GenericError.popup(err.message);
      return;
    }
    this.close();
    await this.$router.push("/");
  }

  protected showLoader(message = "Loading..."): void {
    if (this.loader) {
      this.loader.hide();
    }
    this.loader = this.$loading.show(
      { opacity: 1 },
      {
        before: this.$createElement("h1", message)
      }
    );
  }

  protected closeLoader(): void {
    setTimeout(() => {
      this.loader.hide();
    }, 300);
  }
}
